import { useCallback, useState } from 'react';

import { AxiosError } from '@/api/client';
import { confirmUpload, presignUpload, STORAGE_DISABLED_CODE, type FileAttachment } from '@/api/files.api';

/**
 * Orchestrates a single presigned direct-to-bucket upload:
 *
 *   presign → PUT bytes straight to the bucket → confirm
 *
 * The bytes never transit the API. Upload progress comes from an
 * XMLHttpRequest (fetch can't report upload progress), so callers get a
 * real 0→1 bar. When the server reports storage is unconfigured (503
 * `storage_disabled`) the hook surfaces `storageDisabled` so the UI can
 * show a friendly disabled state instead of an error — mirroring the
 * MOCK_AUTH / no-AI-key posture elsewhere in the app.
 */

export type UploadPhase = 'idle' | 'presigning' | 'uploading' | 'confirming' | 'done' | 'error';

export interface UploadState {
  phase: UploadPhase;
  /** 0..1 for the byte transfer; 1 once confirming. */
  progress: number;
  error: string | null;
  /** True when the API reports storage is not configured (503). */
  storageDisabled: boolean;
}

export interface UseFileUpload {
  state: UploadState;
  /** Runs the full presign→PUT→confirm flow; resolves to the row or null. */
  upload: (file: File) => Promise<FileAttachment | null>;
  reset: () => void;
}

const IDLE: UploadState = { phase: 'idle', progress: 0, error: null, storageDisabled: false };

/** PUT the file bytes directly to the presigned bucket URL, with progress. */
function putToBucket(uploadUrl: string, file: File, onProgress: (fraction: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    // The signed URL pins this content-type; the header must match or the
    // bucket rejects the PUT with a signature error.
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${String(xhr.status)})`));
    };
    xhr.onerror = () => {
      reject(new Error('Upload failed — could not reach storage.'));
    };
    xhr.send(file);
  });
}

function isStorageDisabled(err: unknown): boolean {
  return (
    err instanceof AxiosError &&
    err.response?.status === 503 &&
    (err.response.data as { error?: string } | undefined)?.error === STORAGE_DISABLED_CODE
  );
}

function messageFor(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    if (data?.error === 'file_too_large') return 'That file is larger than the upload limit.';
    if (data?.error === 'unsupported_type') return 'That file type is not allowed.';
    if (typeof data?.message === 'string') return data.message;
  }
  if (err instanceof Error) return err.message;
  return 'Upload failed.';
}

export function useFileUpload(
  opts: { entityType?: string | null; entityId?: string | null } = {},
): UseFileUpload {
  const [state, setState] = useState<UploadState>(IDLE);
  const { entityType, entityId } = opts;

  const upload = useCallback(
    async (file: File): Promise<FileAttachment | null> => {
      setState({ phase: 'presigning', progress: 0, error: null, storageDisabled: false });
      try {
        const { fileId, uploadUrl } = await presignUpload({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          entityType: entityType ?? null,
          entityId: entityId ?? null,
        });

        setState((s) => ({ ...s, phase: 'uploading' }));
        await putToBucket(uploadUrl, file, (fraction) => {
          setState((s) => ({ ...s, progress: fraction }));
        });

        setState((s) => ({ ...s, phase: 'confirming', progress: 1 }));
        const attachment = await confirmUpload(fileId);

        setState({ phase: 'done', progress: 1, error: null, storageDisabled: false });
        return attachment;
      } catch (err) {
        const storageDisabled = isStorageDisabled(err);
        setState({
          phase: 'error',
          progress: 0,
          error: storageDisabled ? null : messageFor(err),
          storageDisabled,
        });
        return null;
      }
    },
    [entityType, entityId],
  );

  const reset = useCallback(() => {
    setState(IDLE);
  }, []);

  return { state, upload, reset };
}
